import { useState, useEffect, useRef } from 'react';
import { useReadersStore } from '@/store/readers.store';
import { useRewardsStore } from '@/store/rewards.store';
import { rewardsService } from '@/services/rewards.service';
import { EggSvg } from './EggSvg';
import { CreatureSvg } from './CreatureSvg';
import { RarityPill } from './RarityPill';
import { RARITY_CONFIG } from '@/constants/rewards';
import type { EggTierId, Rarity } from '@/types/rewards';

export function RevealModal() {
  const result = useRewardsStore((s) => s.revealResult);
  const hatchingEggId = useRewardsStore((s) => s.hatchingEggId);
  const setRevealResult = useRewardsStore((s) => s.setRevealResult);
  const setHatchingEggId = useRewardsStore((s) => s.setHatchingEggId);
  const setCollection = useRewardsStore((s) => s.setCollection);
  const setSummary = useRewardsStore((s) => s.setSummary);
  const selectedReader = useReadersStore((s) => s.selectedReader());
  const activeEggs = useRewardsStore((s) => s.activeEggs);

  const [phase, setPhase] = useState<'crack' | 'reveal' | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const hatchingEgg = activeEggs.find((e) => e.id === hatchingEggId);

  // Open modal when hatching starts
  useEffect(() => {
    if (hatchingEggId && !result) {
      setPhase('crack');
      dialogRef.current?.showModal();
    }
  }, [hatchingEggId, result]);

  // Transition from crack to reveal
  useEffect(() => {
    if (result && phase === 'crack') {
      const timer = setTimeout(() => setPhase('reveal'), 1100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [result, phase]);

  const handleDismiss = () => {
    setPhase(null);
    setRevealResult(null);
    setHatchingEggId(null);
    dialogRef.current?.close();

    // Refresh collection + summary so the nursery shows the new creature
    if (selectedReader) {
      rewardsService
        .getCollection(selectedReader.id)
        .then(setCollection)
        .catch(() => {});
      rewardsService
        .getSummary(selectedReader.id)
        .then(setSummary)
        .catch(() => {});
    }
  };

  // Esc to close during reveal
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase === 'reveal') handleDismiss();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [phase]);

  if (!phase) return null;

  const rarity = result?.creature.rarity as Rarity | undefined;
  const rarityConfig = rarity ? RARITY_CONFIG[rarity] : null;

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      style={{
        margin: 'auto',
        border: 'none',
        background: 'transparent',
        maxWidth: '420px',
        width: '100%',
      }}
      aria-labelledby={result ? 'reveal-creature-name' : undefined}
    >
      <div
        className="rounded-3xl bg-white shadow-2xl overflow-hidden w-full animate-reveal-in"
        style={
          rarityConfig && phase === 'reveal'
            ? {
                boxShadow: `0 0 60px ${rarityConfig.glow}, 0 25px 50px rgba(0,0,0,0.15)`,
              }
            : undefined
        }
      >
        {phase === 'crack' && (
          <div className="flex flex-col items-center py-16 px-8">
            <div className="animate-egg-shake">
              <EggSvg
                tierId={
                  (hatchingEgg?.tierId ?? result?.creature.fromTier ?? 'speckled') as EggTierId
                }
                size={100}
              />
            </div>
            <p className="mt-6 text-lg font-medium text-stone-500">Cracking&hellip;</p>
          </div>
        )}

        {phase === 'reveal' && result && (
          <div className="flex flex-col items-center py-10 px-8 text-center">
            {/* Eyebrow */}
            <p
              className="text-xs font-bold tracking-[0.15em] uppercase mb-4"
              style={{ color: rarityConfig?.color }}
            >
              {result.isNewDiscovery
                ? '\u2726 New Discovery \u2726'
                : `\u2605 ${rarityConfig?.label} Hatched`}
            </p>

            {/* Creature pop-in */}
            <div
              className="mb-4 animate-reveal-pop"
              style={{
                background: rarityConfig
                  ? `radial-gradient(circle, ${rarityConfig.glow}, transparent 70%)`
                  : undefined,
                borderRadius: '50%',
                padding: '20px',
              }}
            >
              <CreatureSvg speciesId={result.creature.speciesId} size={140} />
            </div>

            <h2
              id="reveal-creature-name"
              className="text-3xl font-display font-bold text-stone-900 mb-2"
            >
              {result.creature.name}
            </h2>
            <RarityPill rarity={result.creature.rarity} className="mb-3" />
            <p className="text-sm text-stone-500 mb-4">{result.creature.tagline}</p>

            {/* Lore box */}
            <div className="bg-stone-50 rounded-xl px-5 py-4 text-sm text-stone-600 text-left leading-relaxed mb-6 w-full">
              {result.creature.lore}
            </div>

            <button
              onClick={handleDismiss}
              className="rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-2.5 text-sm transition-colors"
            >
              Welcome to the collection &rarr;
            </button>
          </div>
        )}
      </div>
    </dialog>
  );
}
